import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import * as Slot from '@radix-ui/react-slot';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-[var(--button-radius)] text-[length:var(--button-font-size)] font-[var(--button-font-weight)] whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-white hover:bg-[var(--button-primary-hover)]',
        destructive:
          'bg-destructive text-white hover:bg-[var(--destructive-hover)] focus-visible:ring-[var(--destructive-hover)] dark:focus-visible:ring-[var(--destructive-hover)] dark:bg-destructive dark:hover:bg-[var(--destructive-hover)]',
        outline:
          'border-[var(--input-border)] bg-transparent text-[var(--foreground)] hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-[var(--button-secondary-hover)] dark:bg-secondary dark:hover:bg-[var(--button-secondary-hover)]',
        ghost:
          'bg-transparent hover:bg-[var(--button-ghost-hover-background)] text-[var(--button-ghost-foreground)] hover:text-[var(--button-ghost-foreground)] dark:hover:bg-[var(--button-ghost-hover-background)]',
        link: 'text-[var(--link-foreground)] hover:text-[var(--link-hover-foreground)] underline-offset-4 hover:underline dark:hover:text-[var(--link-hover-foreground)]',
      },
      size: {
        default: 'h-[var(--button-height)] px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 rounded-[var(--button-radius)] px-2 text-[length:var(--small-font-size)] has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-[var(--button-radius)] px-3 text-[length:var(--small-font-size)] has-[>svg]:px-2.5',
        lg: 'h-10 rounded-[var(--button-radius)] px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-[var(--button-radius)] [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type ButtonProps = Readonly<
  React.ComponentPropsWithoutRef<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>;

/**
 * Button primitive. Wrapped in `forwardRef` so a parent ref reaches the
 * rendered element under React 18 as well as React 19. Radix `asChild`
 * triggers (DropdownMenu, Tooltip, Popover, …) pass a ref through their
 * Slot to this component to anchor their popper; on React 18 a plain
 * function component silently drops that ref, leaving the popper with no
 * anchor to measure (it never positions). `forwardRef` works on both
 * major versions, so it is the host-compatible form.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'default', size = 'default', asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
});

export { Button, buttonVariants };
